import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Layout } from './Layout';

interface DashboardGuardProps {
  children: React.ReactNode;
}

const DASHBOARD_AUTH_KEY = 'dashboard_last_auth';
const DASHBOARD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutos

export const DashboardGuard: React.FC<DashboardGuardProps> = ({ children }) => {
  const { verifyPassword, user } = useAuth();
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Validar sesión inicial al montar
  useEffect(() => {
    const lastAuth = sessionStorage.getItem(DASHBOARD_AUTH_KEY);
    if (lastAuth) {
      const timeElapsed = Date.now() - parseInt(lastAuth, 10);
      if (timeElapsed < DASHBOARD_TIMEOUT_MS) {
        setIsVerified(true);
      } else {
        sessionStorage.removeItem(DASHBOARD_AUTH_KEY);
      }
    }
  }, []);

  // Controlar expiración mientras se está en la página
  useEffect(() => {
    if (isVerified) {
      const lastAuth = sessionStorage.getItem(DASHBOARD_AUTH_KEY);
      const timeElapsed = lastAuth ? Date.now() - parseInt(lastAuth, 10) : 0;
      const timeRemaining = DASHBOARD_TIMEOUT_MS - timeElapsed;

      if (timeRemaining <= 0) {
        handleExpire();
        return;
      }

      const timeoutId = setTimeout(handleExpire, timeRemaining);
      return () => clearTimeout(timeoutId);
    }
  }, [isVerified]);

  const handleExpire = () => {
    setIsVerified(false);
    sessionStorage.removeItem(DASHBOARD_AUTH_KEY);
    toast('Sesión de dashboard expirada. Por favor, ingresa tu contraseña nuevamente.', { icon: '🔒' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsLoading(true);
    setError('');

    try {
      const isValid = await verifyPassword(password);
      if (isValid) {
        setIsVerified(true);
        sessionStorage.setItem(DASHBOARD_AUTH_KEY, Date.now().toString());
        setPassword('');
        toast.success('Acceso concedido al Dashboard');
      } else {
        setError('Contraseña incorrecta');
        toast.error('Contraseña incorrecta');
      }
    } catch (err: any) {
      setError(err.message || 'Error verificando contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  // Si no es un usuario ADMIN, no debería estar aquí, pero lo dejamos pasar o bloqueamos según sea la regla global
  // La regla dice que a los administradores se les pedirá contraseña para el dashboard.
  if (user?.rol_usr !== 'ADMIN') {
      return <>{children}</>;
  }

  if (isVerified) {
    return <>{children}</>;
  }

  return (
    <Layout>
      <div className="flex flex-col justify-center items-center h-[calc(100vh-100px)] bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6">
            <LockClosedIcon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Restringido</h2>
          <p className="text-gray-500 mb-8 text-sm">
            El Dashboard contiene información sensible. Por seguridad, ingresa tu contraseña para visualizarlo.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="Ingresa tu contraseña"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm font-medium text-left bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !password}
              className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Desbloquear Dashboard'
              )}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};
